import type { Component } from 'solid-js';
import { LegacyRedirect } from '../components/LegacyRedirect';
import { AccountAudit } from '../routes/AccountAudit';
import { AccountKeys } from '../routes/AccountKeys';
import { AccountPromptVars } from '../routes/AccountPromptVars';
import { AccountSso } from '../routes/AccountSso';
import { AccountTemplates } from '../routes/AccountTemplates';
import { AccountTokens } from '../routes/AccountTokens';
import { AccountWebhooks } from '../routes/AccountWebhooks';
import { AdminAccessRequests } from '../routes/AdminAccessRequests';
import { AdminGoals } from '../routes/AdminGoals';
import { AdminGrants } from '../routes/AdminGrants';
import { AdminInvitations } from '../routes/AdminInvitations';
import { AdminOrgs } from '../routes/AdminOrgs';
import { AdminOss } from '../routes/AdminOss';
import { AdminOverview } from '../routes/AdminOverview';
import { AdminRun } from '../routes/AdminRun';
import { AdminUser } from '../routes/AdminUser';
import { AdminUsers } from '../routes/AdminUsers';
import { Agents } from '../routes/Agents';
import { Artifacts } from '../routes/Artifacts';
import { AuditSession } from '../routes/AuditSession';
import { Billing } from '../routes/Billing';
import { Blockers } from '../routes/Blockers';
import { Ci } from '../routes/Ci';
import { ConnectedLogins } from '../routes/ConnectedLogins';
import { Dashboards } from '../routes/Dashboards';
import { DashboardView } from '../routes/DashboardView';
import { DevopsReport } from '../routes/DevopsReport';
import { Digests } from '../routes/Digests';
import { Donations } from '../routes/Donations';
import { DonationsReceived } from '../routes/DonationsReceived';
import { EmailRoutes } from '../routes/EmailRoutes';
import { EpicView } from '../routes/EpicView';
import { Fleet } from '../routes/Fleet';
import { FleetAudit } from '../routes/FleetAudit';
import { FleetWork } from '../routes/FleetWork';
import { GithubToken } from '../routes/GithubToken';
import { Home } from '../routes/Home';
import { Integrations } from '../routes/Integrations';
import { Invite } from '../routes/Invite';
import { MyKeys } from '../routes/MyKeys';
import { NotFound } from '../routes/NotFound';
import { Notifications } from '../routes/Notifications';
import { Onboarding } from '../routes/Onboarding';
import { OrgMembers } from '../routes/OrgMembers';
import { Pending } from '../routes/Pending';
import { Plans } from '../routes/Plans';
import { PrimitivesDemo } from '../routes/PrimitivesDemo';
import { RepoDetail } from '../routes/RepoDetail';
import { Repos } from '../routes/Repos';
import { Squads } from '../routes/Squads';
import { SystemPrompts } from '../routes/SystemPrompts';
import { Terminal } from '../routes/Terminal';
import { INVITE_PATH } from './invitations';

// Single source of truth for the route table.
// Keep this small. New routes go here and are picked up by <App/>.
// Param schema (`:full_name`, `:session_id`) matches `docs/idea/dashboard.md`.
//
// URL-based org scoping (#460): every account-scoped screen lives under the
// `/org/:org` base (`ORG_BASE_PATH`) — the org segment IS the tenant, GitHub
// style. `<App/>` nests `ORG_ROUTE_TABLE` under an `<OrgScope/>` wrapper that
// feeds the segment into query keys + the `X-Dz-Account` header.
//
// Reserved: `/org/:org/:repo` — custom per-repo dashboards (future, per #460).
// Keep new org-scoped sections out of that single-segment namespace (use a
// static section prefix like `/repos/…`, `/fleet/…`).

/**
 * One screen, stated ONCE. What else names a screen reads it from here: `<App/>`
 * mounts `component` at `path`, the command palette (`lib/command-palette.ts`)
 * reads `path` and `label`, the sidebar rail (`lib/rail.ts`) reads those and
 * `group`, and the axe sweep (`testing/axe-cases-org.tsx`) mounts `component`.
 */
export type RouteEntry<K extends string = string> = {
  readonly path: string;
  readonly component: Component;
  readonly key: K;
  /**
   * The screen's name as an i18n key — the one label the palette, the rail and
   * the topbar breadcrumb all render. REQUIRED, so adding a screen without
   * deciding whether it is a destination is a compile error rather than a
   * screen reachable only by typing its URL.
   *
   * `null` is "not a destination", and its rule is mechanical: a path carrying
   * a param or a wildcard has no single address to jump to
   * (`command-palette.test.ts` pins that against these tables). `invite` is the
   * one static exception — see its entry.
   */
  readonly label: string | null;
  /**
   * The sidebar group the screen lives in, or `null` for "not on the rail".
   * REQUIRED for the same reason `label` is: a new screen must decide which JOB
   * it serves, so it lands in an existing group instead of growing a new tab
   * (the canvas rule, #4195). The rail (`lib/rail.ts`) reads it; the row ORDER
   * inside a group is the rail's, not this table's.
   *
   * A non-null group needs a non-null `label`, and every grouped route must be a
   * row of its group — `lib/rail.ts` throws at import otherwise, so every test
   * loading the shell fails.
   */
  readonly group: NavGroup | null;
  /**
   * Full bleed (#4395): drop the `.page` wrapper `<AppShell/>` puts around
   * every route's content, so a window-shaped screen (the Terminal console)
   * fills the content area itself instead of sitting inside the shared page
   * rhythm meant for a title + cards. Omitted (not `false`) on every other
   * route — `AppShell.test.tsx` pins that a non-bleed route still gets `.page`.
   */
  readonly bleed?: true;
};

/**
 * The jobs the rail is organised by (the design canvas, 08 / APP): look around,
 * watch the work, decide what is owed, prove what happened and configure it.
 * `settings` is the fifth section, set-once configuration; `admin` is the admin
 * host's single section. Every section folds (`components/RailGroups.tsx`).
 */
export type NavGroup = 'orient' | 'watch' | 'decide' | 'prove' | 'settings' | 'admin';

/**
 * Identity, typed: it freezes the entry KEYS as a literal union while widening
 * `component` back to `Component`. Both halves matter. `as const` alone would
 * keep each screen's concrete signature, which solid-router's
 * `<Route component>` (`RouteSectionProps`) refuses; without it the keys widen
 * to `string` and the axe cases (`testing/axe-cases-{org,top}.tsx`) can no
 * longer key an EXHAUSTIVE record off this table — so a new screen would
 * silently ship unswept.
 */
function routeTable<const T extends readonly RouteEntry[]>(
  entries: T,
): readonly RouteEntry<T[number]['key']>[] {
  return entries;
}

/** Base path the org-scoped table nests under. */
export const ORG_BASE_PATH = '/org/:org' as const;

/** Account-scoped screens — mounted under `ORG_BASE_PATH` (`/org/<login>/…`). */
export const ORG_ROUTE_TABLE = routeTable([
  { key: 'home', path: '/', component: Home, label: 'nav.home', group: 'orient' },
  // Read-only agent model picks (#5209, epic #5200) — first in WATCH: which
  // model each platform agent uses, what it runs on right now, and who last
  // changed it. The picker, saves and the drawer are #5210/#5211.
  {
    key: 'agents',
    path: '/fleet/agents',
    component: Agents,
    label: 'nav.agents',
    group: 'watch',
  },
  // The org's live console (#4395, epic #4391) — a projection over
  // `audit_events`, rendered as a terminal window. Full bleed: it fills the
  // content area itself (window chrome + status line + log), never `.page`'s
  // title-and-cards rhythm.
  {
    key: 'terminal',
    path: '/terminal',
    component: Terminal,
    label: 'nav.terminal',
    group: 'watch',
    bleed: true,
  },
  {
    key: 'onboarding',
    path: '/onboarding',
    component: Onboarding,
    label: 'nav.finish_setup',
    group: null,
  },
  { key: 'repos', path: '/repos', component: Repos, label: 'nav.repos', group: 'orient' },
  {
    key: 'repo_detail',
    path: '/repos/:full_name/*tab',
    component: RepoDetail,
    label: null,
    group: null,
  },
  {
    key: 'account_keys',
    path: '/account/keys',
    component: AccountKeys,
    label: 'nav.keys',
    group: 'prove',
  },
  {
    key: 'my_keys',
    path: '/account/my-keys',
    component: MyKeys,
    label: 'nav.my_keys',
    group: 'settings',
  },
  {
    key: 'github_token',
    path: '/account/github',
    component: GithubToken,
    label: 'nav.github',
    group: 'settings',
  },
  // The person's own logins (#466): GitHub plus each linked org SSO identity,
  // and the link form per org IdP. The path is a CONTRACT: the API's link
  // callback lands on the bare `/account/logins?linked=…|link_error=…`, which
  // the legacy catch-all re-roots under the person's org with the query kept.
  {
    key: 'logins',
    path: '/account/logins',
    component: ConnectedLogins,
    label: 'nav.logins',
    group: 'settings',
  },
  {
    key: 'account_tokens',
    path: '/account/tokens',
    component: AccountTokens,
    label: 'nav.api_tokens',
    group: 'settings',
  },
  {
    key: 'system_prompts',
    path: '/account/prompts',
    component: SystemPrompts,
    label: 'nav.system_prompts',
    group: 'settings',
  },
  // Agent config the fleet runs on (`write:runners`), human doors onto
  // `/v1/templates` + `/v1/prompt-vars` — both had a REST route and an MCP verb
  // and no screen.
  {
    key: 'account_templates',
    path: '/account/templates',
    component: AccountTemplates,
    label: 'nav.templates',
    group: 'settings',
  },
  {
    key: 'account_prompt_vars',
    path: '/account/prompt-vars',
    component: AccountPromptVars,
    label: 'nav.prompt_vars',
    group: 'settings',
  },
  {
    key: 'account_webhooks',
    path: '/account/webhooks',
    component: AccountWebhooks,
    label: 'nav.webhooks',
    group: 'settings',
  },
  {
    key: 'notifications_settings',
    path: '/account/notifications',
    component: Notifications,
    label: 'nav.notifications',
    group: 'prove',
  },
  {
    key: 'email_routes',
    path: '/account/email-routes',
    component: EmailRoutes,
    label: 'nav.email_routes',
    group: 'settings',
  },
  {
    key: 'integrations',
    path: '/account/integrations',
    component: Integrations,
    label: 'nav.integrations',
    group: 'settings',
  },
  {
    key: 'account_sso',
    path: '/account/sso',
    component: AccountSso,
    label: 'nav.sso',
    group: 'settings',
  },
  {
    key: 'account_members',
    path: '/account/members',
    component: OrgMembers,
    label: 'nav.members',
    group: 'settings',
  },
  // Squads (#6780, #7010 D12): admin-named groups of repos, beside Members.
  {
    key: 'account_squads',
    path: '/account/squads',
    component: Squads,
    label: 'nav.squads',
    group: 'settings',
  },
  {
    key: 'billing',
    path: '/account/billing',
    component: Billing,
    label: 'nav.billing',
    group: 'prove',
  },
  { key: 'audit', path: '/audit', component: AccountAudit, label: 'nav.audit', group: 'prove' },
  {
    key: 'audit_session',
    path: '/audit/:session_id',
    component: AuditSession,
    label: null,
    group: null,
  },
  { key: 'digests', path: '/digests', component: Digests, label: 'nav.digests', group: 'orient' },
  // Agent-produced objects (arc 101 slice 08) — the third capability that had a
  // REST route and an MCP verb and no screen.
  {
    key: 'artifacts',
    path: '/artifacts',
    component: Artifacts,
    label: 'nav.artifacts',
    group: 'watch',
  },
  // The pull requests policy holds for a human decision (the canvas DECIDE
  // group, #4195): APPROVE / SKIP through the existing per-PR endpoints only.
  // Home shows the head of this queue; this is all of it.
  { key: 'pending', path: '/pending', component: Pending, label: 'nav.pending', group: 'decide' },
  // The answerable hold (plan doc 07 step 5) — an inbox of owed answers, next to
  // the fleet runs that raised them. The path is a CONTRACT: the fleet
  // notification deep-links `/blockers?blocker=<blockerId>`.
  {
    key: 'blockers',
    path: '/blockers',
    component: Blockers,
    label: 'nav.blockers',
    group: 'decide',
  },
  { key: 'fleet', path: '/fleet', component: Fleet, label: 'nav.fleet', group: 'watch' },
  {
    key: 'fleet_run',
    path: '/fleet/runs/:run_id',
    component: FleetAudit,
    label: null,
    group: null,
  },
  // What a run DELIVERED (arc 101 slice 06) — the sibling of the run timeline
  // above. Addressed by TASK, not by run, because that is the projection's rule:
  // the latest run wins for the report while filings and blockers span every
  // attempt, so a per-run address could not answer it.
  {
    key: 'devops_report',
    path: '/fleet/tasks/:task_id/report',
    component: DevopsReport,
    label: null,
    group: null,
  },
  // The epic INDEX, and the epic itself. `plan.list` had a REST route and an MCP
  // action and no screen, so an account's plans were reachable only one id at a
  // time, from whichever task row happened to belong to one. The path nests under
  // Fleet and Fleet links in; since the canvas rail (#4195) it is also a WATCH
  // row of its own, because "which plans are running" is a thing people watch.
  {
    key: 'plans',
    path: '/fleet/plans',
    component: Plans,
    label: 'fleet.plans.link',
    group: 'watch',
  },
  // All the org's WORK, off "See all work" on `/fleet` (#6788, owner ruling G12):
  // the full queue, moved unchanged, with `?machine=` narrowing it to one machine.
  // NO RAIL ROW: it nests under Fleet, and `rail.ts`'s longest-prefix match keeps
  // Fleet lit here. A real label all the same (the `onboarding` shape), because
  // the command palette offers every static org route by its label.
  {
    key: 'fleet_work',
    path: '/fleet/work',
    component: FleetWork,
    label: 'fleet.work.title',
    group: null,
  },
  {
    key: 'fleet_plan',
    path: '/fleet/plans/:plan_id',
    component: EpicView,
    label: null,
    group: null,
  },
  { key: 'ci', path: '/ci', component: Ci, label: 'nav.ci', group: 'watch' },
  // OSS donations (arc 101 slice 11): the two SIDES of a gift, authorized against
  // different principals server-side — what this account gave, and what repos it
  // owns were given. The inbox nests under `/donations` so ONE rail entry (in the
  // folded Settings disclosure) reaches both; one person is usually both, and two
  // rows for one noun reads like two features.
  {
    key: 'donations',
    path: '/donations',
    component: Donations,
    label: 'nav.donations',
    group: 'settings',
  },
  {
    key: 'donations_received',
    path: '/donations/received',
    component: DonationsReceived,
    label: 'donations.received_title',
    group: null,
  },
  // JSON-defined boards (arc 101 slice 09): the scope-router index + the generic
  // renderer route that draws any board by id.
  {
    key: 'dashboards',
    path: '/dashboards',
    component: Dashboards,
    label: 'nav.dashboards',
    group: 'orient',
  },
  {
    key: 'dashboard_view',
    path: '/dashboards/:id',
    component: DashboardView,
    label: null,
    group: null,
  },
  // Storybook-style showcase for the three dashboard overlay primitives
  // (Popover, BottomSheet, Toast) — `/:org/primitives-demo`. Group is null
  // (no rail row — a demo is not a job), and the page is not bleed, so it
  // sits inside the normal `.page` rhythm. The label is the one key the
  // rail, the command palette and the topbar breadcrumb all read.
  {
    key: 'primitives_demo',
    path: '/primitives-demo',
    component: PrimitivesDemo,
    label: 'nav.primitives_demo',
    group: null,
  },
  { key: 'not_found', path: '*', component: NotFound, label: null, group: null },
]);

/** Top-level (non-org) screens: the platform-admin radar + the legacy catch-all. */
export const TOP_ROUTE_TABLE = routeTable([
  // admin.developerz.ai (same SPA, sibling host). Each is gated behind
  // <AdminGate/> → non-staff hit the closed-beta wall; only `developerz-ai`
  // staff (`is_platform_admin`) see the platform radar + org/user management.
  // Deliberately NOT org-scoped — the radar is cross-account by definition.
  {
    key: 'admin_overview',
    path: '/admin',
    component: AdminOverview,
    label: 'nav.admin_overview',
    group: 'admin',
  },
  // The autonomous goal monitor: developerz.ai's OWN roadmap, graded from live
  // GitHub state rather than from `docs/plans/**\/status.yml` (which is
  // measurably stale — see `apps/api/src/deps/goals.ts`).
  {
    key: 'admin_goals',
    path: '/admin/goals',
    component: AdminGoals,
    label: 'nav.admin_goals',
    group: 'admin',
  },
  {
    key: 'admin_orgs',
    path: '/admin/orgs',
    component: AdminOrgs,
    label: 'nav.admin_orgs',
    group: 'admin',
  },
  {
    key: 'admin_users',
    path: '/admin/users',
    component: AdminUsers,
    label: 'nav.admin_users',
    group: 'admin',
  },
  // The access funnel (arc 101 slice 01): the waitlist inbox an operator invites
  // from, and the invitations that inviting mints. Both platform objects with no
  // tenant to fence on, so TOP-LEVEL like the rest of the console.
  {
    key: 'admin_access_requests',
    path: '/admin/access-requests',
    component: AdminAccessRequests,
    label: 'nav.admin_access_requests',
    group: 'admin',
  },
  {
    key: 'admin_invitations',
    path: '/admin/invitations',
    component: AdminInvitations,
    label: 'nav.admin_invitations',
    group: 'admin',
  },
  // OSS-verification queue (#68): applications filed from the Billing route.
  {
    key: 'admin_oss',
    path: '/admin/oss',
    component: AdminOss,
    label: 'nav.admin_oss',
    group: 'admin',
  },
  // Entitlement grants (epic #1259) — the console's only REST write plane, and
  // the audited replacement for the psql session that used to hand out free
  // capacity. Subject-addressed: nothing reads until an org or a person is
  // picked.
  {
    key: 'admin_grants',
    path: '/admin/grants',
    component: AdminGrants,
    label: 'nav.admin_grants',
    group: 'admin',
  },
  // Cross-org run lookup (#2481). TOP-LEVEL and that is the mechanism, not a
  // filing decision: on `/admin/*` `orgFromPath` resolves nothing, so `apiFetch`
  // sends no `X-Dz-Account` header and the server resolves the run's OWNER
  // itself. Under `/org/:org/...` the header would re-fence the read to whichever
  // org the URL named — which is the prod failure this route exists to fix.
  { key: 'admin_run', path: '/admin/runs/:run_id', component: AdminRun, label: null, group: null },
  // One person across every org (#5727). Not a rail row: it needs an id, and the
  // rail's prefix match already marks `Users` current under `/admin/users/…`.
  {
    key: 'admin_user',
    path: '/admin/users/:id',
    component: AdminUser,
    label: null,
    group: null,
  },
  // Invitation landing (arc 101 slice 01) — the URL every invitation email has
  // minted since #2351 (`invitationAcceptUrl`), which until now fell through to
  // `legacy_redirect` and ended at the org `NotFound`.
  //
  // TOP-LEVEL, and that is mechanism rather than filing: an invitee may belong
  // to NO org yet, so there is no segment to nest it under — and off `/org/…`
  // `apiFetch` sends no `X-Dz-Account` header, so the server resolves the
  // invitation's own account instead of re-fencing the redeem to whichever org
  // the URL named (the `admin_run` reasoning, for a different reader).
  //
  // Static, and still not a palette destination: `/invite` does nothing without
  // the `?token=` off an invitation email, which nobody typing into the palette
  // holds — offering it would offer a page that can only refuse.
  { key: 'invite', path: INVITE_PATH, component: Invite, label: null, group: null },
  // Pre-#460 bare paths (`/fleet`, `/repos/…`) redirect into the user's
  // last-or-primary org; genuinely unknown paths end at the org NotFound.
  { key: 'legacy_redirect', path: '*', component: LegacyRedirect, label: null, group: null },
]);

/** Every account-scoped screen's key, as a union. See `routeTable` above. */
export type OrgRouteKey = (typeof ORG_ROUTE_TABLE)[number]['key'];

/** Every top-level screen's key, as a union. See `routeTable` above. */
export type TopRouteKey = (typeof TOP_ROUTE_TABLE)[number]['key'];

/**
 * `path` (a `RouteEntry.path`, as `:param` / `*rest` spell it) as a matcher
 * for `orgRouteSection`'s output. Segment-wise, not a blind regex escape: a
 * `:name` or `*name` segment matches anything, everything else matches
 * literally — the same two wildcards solid-router's own paths use, so a route
 * added here needs no second dialect learned to make it `bleed`.
 */
function segmentsMatch(pattern: string, section: string): boolean {
  const pat = pattern.split('/');
  const sec = section.split('/');
  if (pat.length !== sec.length) return false;
  return pat.every((seg, i) => seg.startsWith(':') || seg.startsWith('*') || seg === sec[i]);
}

/** Every `ORG_ROUTE_TABLE` path marked `bleed: true`, computed once at import. */
const BLEED_PATHS: readonly string[] = ORG_ROUTE_TABLE.filter((r) => r.bleed === true).map(
  (r) => r.path,
);

/**
 * Is the org-scoped section `pathname` resolves to (`lib/org.ts`'s
 * `orgRouteSection`) a `bleed` route's own path? `<AppShell/>`'s only caller —
 * see `RouteEntry.bleed`'s doc for why the mechanism exists at all.
 */
export function isBleedSection(section: string): boolean {
  return BLEED_PATHS.some((p) => segmentsMatch(p, section));
}
